import cx from "classnames";
import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import {
    getDeleteVoteEventData,
    getRenameVoteEventData,
} from "@/appInterface/windows/VoteWindow/PastVote/helpers";
import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { setViewSettings } from "@/canvasController/helpers";
import { SHOW_VOTES_RESULTS_LS_KEY } from "@/constants";
import { PastVoteListItem } from "./PastVoteListItem";
import style from "./pastVote.module.css";

type PastVoteProps = {
    setShowResult: (val: boolean, vote?: VoteEntity) => void;
};

export function PastVote({ setShowResult }: PastVoteProps) {
    const [pastVotes, setPastVotes] = useState<VoteEntity[]>([]);
    const [openId, setOpenId] = useState<string>("");
    const [renameId, setRenameId] = useState<string>("");

    const [showVotesResults, setShowVotesResults] = React.useState(
        canv.canvasController.showVotesResults,
    );

    const handleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        setShowVotesResults(e.target.checked);
        canv.canvasController.showVotesResults = e.target.checked;
        setViewSettings(SHOW_VOTES_RESULTS_LS_KEY, e.target.checked);
    };

    const handleRename = (voteUID: string, text: string) => {
        canv.intercom.eventMessage(getRenameVoteEventData(voteUID, text));
    };

    const handleRemove = (voteUID: string) => {
        canv.intercom.eventMessage(
            getDeleteVoteEventData(
                voteUID,
                canv.getInitDataProp("userToken"),
                canv.isBoardModeOwner(),
            ),
        );
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const sortedVotes = [...canv.canvasController.pastVotes].sort((a, b) => {
            if (!a.start || !b.start) return 1;
            return +new Date(b.start) - +new Date(a.start);
        });
        setPastVotes(sortedVotes);
    }, [canv.appInterface.voteWindow.rerenderTrigger]);

    return (
        <div className={style.pastVoteWrapper}>
            <div className={style.pastVoteSwitchWrapper}>
                <div className={style.pastVoteSlider}>
                    <p>Показывать результаты на доске</p>
                    <label className="switch">
                        <input
                            className={style.showResultsOnBoard}
                            type="checkbox"
                            onChange={handleSwitch}
                            checked={showVotesResults}
                        />
                        <span className="slider round" />
                    </label>
                </div>
            </div>
            <div className={cx(style.pastVotesListWrapper, "scrollable")}>
                {pastVotes.map((vote) => (
                    <VoteListItemWrapper
                        key={vote.UID}
                        vote={vote}
                        onRename={handleRename}
                        onRemove={handleRemove}
                        onClick={setShowResult}
                        openId={openId}
                        setOpenId={setOpenId}
                        renameId={renameId}
                        setRenameId={setRenameId}
                    />
                ))}
            </div>
        </div>
    );
}

type VoteListItemWrapperProps = {
    vote: VoteEntity;
    onRename: (voteUID: string, text: string) => void;
    onRemove: (voteUID: string) => void;
    onClick: (val: boolean, vote?: VoteEntity) => void;
    openId: string;
    setOpenId: (id: string) => void;
    renameId: string;
    setRenameId: (id: string) => void;
};

function VoteListItemWrapper({
    vote,
    onRename,
    onRemove,
    onClick,
    openId,
    setOpenId,
    renameId,
    setRenameId,
}: VoteListItemWrapperProps) {
    const handleRenameClick = useCallback(
        (text: string) => onRename(vote.UID, text),
        [vote.UID, onRename],
    );

    const handleRemoveClick = useCallback(() => onRemove(vote.UID), [vote.UID, onRemove]);

    return (
        <PastVoteListItem
            vote={vote}
            onRename={handleRenameClick}
            onRemove={handleRemoveClick}
            onClick={onClick}
            openId={openId}
            setOpenId={setOpenId}
            renameId={renameId}
            setRenameId={setRenameId}
        />
    );
}
